import { helpers } from '@ht/lib-plural-js';
import useConfig from '@/composables/useConfig';
import { VARIABLE_VALUE_TYPES, TIME_OF_DAY_VARIABLES } from '@/js/constants';

const {
  parseEuDate,
  parseIsoDate,
  parseIsoDateTime,
  convertToNumber,
  getVariableValue,
  timeToSeconds,
} = helpers;

function isEmpty(val) {
  if (Array.isArray(val)) {
    return val.length === 0;
  }
  if (typeof val === 'object') {
    return Object.keys(val).length === 0;
  }
  return val === null || val === undefined || val === '';
}

const CONDITION_OPERATIONS = {
  EQUAL: (a, b) => a.toString() === b.toString(),
  NOT_EQUAL: (a, b) => a.toString() !== b.toString(),
  GREATER_THEN: (a, b) => convertToNumber(a) > convertToNumber(b),
  GREATER_THEN_OR_EQUAL: (a, b) => convertToNumber(a) >= convertToNumber(b),
  LESS_THEN: (a, b) => convertToNumber(a) < convertToNumber(b),
  LESS_THEN_OR_EQUAL: (a, b) => convertToNumber(a) <= convertToNumber(b),
  STARTS_WITH: (a, b) => a.startsWith(b),
  ENDS_WITH: (a, b) => a.endsWith(b),
  CONTAINS: (a, b) => a.includes(b),
  NOT_CONTAINS: (a, b) => !a.includes(b),
  ANY: (a) => !isEmpty(a),
  EMPTY: isEmpty,
};

function handleCheckTimeOfDay(value, range, operation) {
  const valueInSeconds = timeToSeconds(value);
  const rangeInSeconds = [timeToSeconds(range[0]), timeToSeconds(range[1])];

  if (operation === 'EQUAL') {
    return CONDITION_OPERATIONS.GREATER_THEN_OR_EQUAL(valueInSeconds, rangeInSeconds[0])
      && CONDITION_OPERATIONS.LESS_THEN_OR_EQUAL(valueInSeconds, rangeInSeconds[1]);
  }

  if (operation === 'NOT_EQUAL') {
    return !(CONDITION_OPERATIONS.GREATER_THEN_OR_EQUAL(valueInSeconds, rangeInSeconds[0])
      && CONDITION_OPERATIONS.LESS_THEN_OR_EQUAL(valueInSeconds, rangeInSeconds[1]));
  }

  return false;
}

export default function useConditions() {
  function checkCondition({ logicalOperation, items }) {
    const { getVariable } = useConfig();

    function checkVariable({ left, operation, right }) {
      const variable = getVariable(left.value);
      const rightVariable = right.type === VARIABLE_VALUE_TYPES.VARIABLE ? getVariable(right.value) : null;
      const leftValue = getVariableValue(variable);
      const rightValue = rightVariable ? getVariableValue(rightVariable) : right.value;

      if (leftValue === rightValue && operation === 'EQUAL') {
        return true;
      }

      if (
        leftValue === undefined
        || leftValue === null
        || rightValue === undefined
        || rightValue === null
      ) {
        if ((leftValue === undefined || leftValue === null) && operation === 'EMPTY') {
          return true;
        }
        return false;
      }

      if (variable.name === 'Date' || variable.name === 'DateIso' || variable.name === 'DateTimeIso') {
        let parseDateFn = parseEuDate;
        if (variable.name === 'DateIso') {
          parseDateFn = parseIsoDate;
        } else if (variable.name === 'DateTimeIso') {
          parseDateFn = parseIsoDateTime;
        }

        return CONDITION_OPERATIONS[operation](
          leftValue,
          right.type === VARIABLE_VALUE_TYPES.PRIMITIVE ? parseDateFn(right.value).getTime() : rightValue,
        );
      }

      if (rightVariable?.name === 'Date' || rightVariable?.name === 'DateIso' || rightVariable?.name === 'DateTimeIso') {
        let parseDateFn = parseEuDate;
        if (variable.name === 'DateIso') {
          parseDateFn = parseIsoDate;
        } else if (variable.name === 'DateTimeIso') {
          parseDateFn = parseIsoDateTime;
        }

        return CONDITION_OPERATIONS[operation](
          typeof leftValue === 'string' ? parseDateFn(leftValue).getTime() : leftValue,
          rightValue,
        );
      }

      if (TIME_OF_DAY_VARIABLES.includes(variable.id)) {
        return handleCheckTimeOfDay(rightValue, leftValue, operation);
      }

      if (TIME_OF_DAY_VARIABLES.includes(rightVariable?.id)) {
        return handleCheckTimeOfDay(leftValue, rightValue, operation);
      }

      if (variable.name === 'Time' || rightVariable?.name === 'Time') {
        return CONDITION_OPERATIONS[operation](
          timeToSeconds(leftValue),
          timeToSeconds(right.type === VARIABLE_VALUE_TYPES.PRIMITIVE ? right.value : rightValue),
        );
      }

      if (variable.name === 'TimeIso' || rightVariable?.name === 'TimeIso') {
        const compareVal = right.type === VARIABLE_VALUE_TYPES.PRIMITIVE ? right.value : rightValue;
        return CONDITION_OPERATIONS[operation](
          (timeToSeconds(leftValue) * 1000 + parseInt(leftValue.split('.')[1], 10)),
          (timeToSeconds(compareVal) * 1000 + parseInt(compareVal.split('.')[1], 10)),
        );
      }

      return CONDITION_OPERATIONS[operation](leftValue, rightValue);
    }

    if (!logicalOperation) {
      const { left, operation, right } = items[0];
      return checkVariable({ left, operation, right });
    }
    const logicalFunc = logicalOperation === 'AND' ? items.every.bind(items) : items.some.bind(items);
    return logicalFunc(({ left, operation, right }) => checkVariable({ left, operation, right }));
  }

  return {
    checkCondition,
  };
}
